import React from 'react';
import { HelpCircle, CheckCircle, AlertCircle, Info } from 'lucide-react';

/**
 * Enhanced Calculator Progress Tracker
 * Shows 5-step progress with completion status
 */
export default function CalculatorProgressTracker({ 
  currentStep = 1, 
  completedSteps = [],
  onStepClick 
}) {
  const steps = [
    { number: 1, name: 'Client Info', description: 'Basic details' },
    { number: 2, name: 'Volume', description: 'Transaction volumes' },
    { number: 3, name: 'Incumbent', description: 'Current pricing' },
    { number: 4, name: 'Proposed', description: 'New pricing' },
    { number: 5, name: 'Distribution', description: 'Transaction mix' }
  ];

  const getStepStatus = (stepNum) => {
    if (completedSteps.includes(stepNum)) return 'complete';
    if (stepNum === currentStep) return 'current';
    if (stepNum < currentStep) return 'incomplete';
    return 'upcoming';
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-slate-900">Calculation Progress</h3>
        <span className="text-sm text-slate-500">
          {completedSteps.length} of 5 steps complete
        </span>
      </div>
      
      {/* Progress Bar */}
      <div className="relative mb-8">
        <div className="absolute top-5 left-0 right-0 h-0.5 bg-slate-200">
          <div 
            className="h-full bg-indigo-600 transition-all duration-500"
            style={{ width: `${(completedSteps.length / 5) * 100}%` }}
          />
        </div>
        
        {/* Steps */}
        <div className="relative flex justify-between">
          {steps.map((step) => {
            const status = getStepStatus(step.number);
            const isClickable = step.number <= currentStep || completedSteps.includes(step.number);
            
            return (
              <button
                key={step.number}
                onClick={() => isClickable && onStepClick && onStepClick(step.number)}
                disabled={!isClickable}
                className={`flex flex-col items-center group ${isClickable ? 'cursor-pointer' : 'cursor-not-allowed'}`}
              >
                {/* Circle */}
                <div className={`
                  w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold mb-2 transition-all
                  ${status === 'complete' ? 'bg-green-500 text-white' : ''}
                  ${status === 'current' ? 'bg-indigo-600 text-white ring-4 ring-indigo-100' : ''}
                  ${status === 'incomplete' ? 'bg-amber-100 text-amber-700' : ''}
                  ${status === 'upcoming' ? 'bg-slate-100 text-slate-400' : ''}
                  ${isClickable && status !== 'current' ? 'hover:scale-110' : ''}
                `}>
                  {status === 'complete' ? (
                    <CheckCircle className="w-5 h-5" />
                  ) : status === 'incomplete' ? (
                    <AlertCircle className="w-5 h-5" />
                  ) : (
                    step.number
                  )}
                </div>
                
                {/* Label */}
                <div className="text-center">
                  <div className={`text-xs font-medium ${
                    status === 'current' ? 'text-indigo-600' :
                    status === 'complete' ? 'text-green-600' :
                    status === 'incomplete' ? 'text-amber-600' :
                    'text-slate-400'
                  }`}>
                    {step.name}
                  </div>
                  <div className="text-xs text-slate-500 hidden sm:block">
                    {step.description}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Quick Tips */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
        <div className="flex gap-2">
          <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <strong>Tip:</strong> Fill out each section completely before moving to the next. 
            Green checkmarks indicate completed sections.
          </div>
        </div>
      </div>
    </div>
  );
}
